import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";
import locationsService from "services/locations-service";


// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";
import DataTable from "e4acomponents/Tables/DataTable";
import ImageModal from "e4acomponents/ImageModal";

// Material UI & Dashboard components
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ImageTwoToneIcon from '@mui/icons-material/ImageTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

export default function LocationsView() {
    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState([]);

    const [imageUrl, setImageUrl] = useState("");
    const [isFetchingImage, setIsFetchingImage] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    useEffect(() => {
        // Fetch locations data from the API
        (async () => {
            setIsLoading(true);
            const response = await locationsService.getAllLocations();
            if (response && response.data && Array.isArray(response.data.attributes)) {
                response.data.attributes.sort((a, b) => {
                    const bKey = a && a.locationKey != null ? String(a.locationKey) : "";
                    const aKey = b && b.locationKey != null ? String(b.locationKey) : "";
                    return aKey.localeCompare(bKey, undefined, { numeric: true, sensitivity: "base" });
                });
            }
            setLocations(response.data.attributes);
        })();
    }, [])

    useEffect(() => {
        setTableData(getRows(locations));
    }, [locations])

    useEffect(() => {
        if (isLoading && tableData.length > 0) {
            setIsLoading(false);
        }
    }, [tableData]);

    // Handles viewing a location image
    const handleViewImage = async (location) => {
        if (!location.imagePath) {
            alert("No image available for this location.");
            return;
        }

        setIsFetchingImage(true);
        setIsImageModalOpen(true);

        try {
            const encodedPath = encodeURIComponent(location.imagePath);
            const response = await locationsService.getLocationImage(encodedPath);
            setImageUrl(response);
        } catch (error) {
            alert("Error fetching image.");
            console.error("Error fetching image:", error);
            setIsImageModalOpen(false);
        } finally {
            setIsFetchingImage(false);
        }
    }

    // Handles closing the image modal
    const handleCloseImageModal = () => {
        setIsImageModalOpen(false);
        setImageUrl("");
    }

    // Handles editing a location
    const handleEditLocation = (location) => {
        navigate(`/location-management/edit-location/${location.id}`, { state: { location } });
    }

    // Transform locations data into table rows
    const getRows = (info) => {
        let updatedInfo = info
            .map((e) => {
                return {
                    id: e._id,
                    locationKey: e.locationKey,
                    warehouse: e.city,
                    locationType: e.locationType,
                    imagePath: e.imagePath,
                    createdAt: new Date(e.created_at).toLocaleDateString("en-US"),
                    updatedAt: new Date(e.updated_at).toLocaleDateString("en-US"),
                }
            });
        return updatedInfo;
    };

    // DataTable confirguration (columns and rows)
    const dataTableData = {
        columns: [
            { Header: "Warehouse", accessor: "warehouse", align: "left" },
            { Header: "Location Type", accessor: "locationType", align: "left" },
            { Header: "Location Key", accessor: "locationKey", align: "left" },
            { Header: "Created At", accessor: "createdAt", align: "left" },
            { Header: "Updated At", accessor: "updatedAt", align: "left" },
            {
                Header: "Actions",
                disableSortBy: true,
                accessor: "actions",
                Cell: ({ row }) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            <Tooltip title="View Location Image">
                                <IconButton
                                    aria-label="view-location-image"
                                    onClick={() => handleViewImage(row.original)}
                                    sx={{ '&:hover': { transform: "scale(1.16)" } }}
                                >
                                    <ImageTwoToneIcon color="info" />
                                </IconButton>
                            </Tooltip>

                            {ability.can("access", "edit-location") && (
                                <Tooltip title="Edit Location" >
                                    <IconButton
                                        aria-label="edit-location"
                                        onClick={() => handleEditLocation(row.original)}
                                        sx={{ '&:hover': { transform: "scale(1.16)" } }}
                                    >
                                        <EditTwoToneIcon color="success" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    )
                }
            }
        ],
        rows: tableData,
    };

    return (

        <DashboardLayout>

            <DashboardNavbar />

            <ImageModal
                mediaURL={imageUrl}
                open={isImageModalOpen}
                onClose={handleCloseImageModal}
                isLoading={isFetchingImage}
            />

            <MDBox pt={6} pb={3}>

                {/* Main Card */}
                <Card>

                    {/* Title and Create Button */}
                    <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">

                        <MDTypography variant="h5" fontWeight="medium">
                            Locations List
                        </MDTypography>

                        {ability.can("access", "add-location") && (
                            <MDButton
                                variant="gradient"
                                color="info"
                                size="small"
                                onClick={() => navigate("/location-management/add-location")}
                            >
                                + Add Location
                            </MDButton>
                        )}
                    </MDBox>

                    {/* DataTable Data */}
                    <MDBox>
                        <DataTable
                            table={dataTableData}
                            isLoading={isLoading}
                            canSearch={true}
                            tableId="locations-management-table"
                        />
                    </MDBox>

                </Card>
            </MDBox>

            <Footer />

        </DashboardLayout>
    )
}


